import { useEffect, useState, useCallback } from 'react';
import { CheckCircle, Clock } from 'lucide-react';
import { supabase } from '../../services/supabase';
import { useAuthStore } from '../../store/authStore';
import { pillarAvg, normalizeDetailed, type PillarDef } from '../../lib/evaluationCatalog';
import { getCatalog, isUnsupportedCatalogVersionError } from '../../lib/evaluationReportV2';
import { fmtDate, scoreColor } from '../../lib/evaluationFormat';
import { esAsignada, motivoDelPendiente } from '../../lib/evaluationPendientes';
import EvaluationForm from '../../components/EvaluationForm';
import { listTrainerHistory, type EvaluationStatus } from '../../services/evaluationDrafts';
import { hoy } from '../../lib/fechaLocal';

// n=8 élite (PR-N8-2) — Pantalla del entrenador (web admin): cola de pendientes
// e historial. El FORMULARIO ya no vive aquí: se extrajo a
// `components/EvaluationForm` en C1 de la Entrega 2, porque la pantalla de
// dirección que crea informes a demanda necesita exactamente el mismo. Lo que
// puntúa, cómo computa las medias y cómo guarda no ha cambiado — ver la
// cabecera del componente. Diseño: docs/journey/diseno-informe-n8-elite.md §4.
// NO incluye el visor (radar/tabla/evolución) — eso es PR-N8-3.

interface Pending {
  student_id: string;
  student_name: string;
  sessions_since_last: number;
  last_eval_date: string | null;
  period_start: string | null;
  period_end: string | null;
  /** mig 288 + 311: de dónde viene la fila, y quién/cuándo si la asignaron. */
  source?: string | null;
  assigned_by?: string | null;
  assigned_at?: string | null;
}
interface HistoryEntry {
  id: string;
  student_id: string;
  student_name: string;
  period_start: string;
  period_end: string | null;
  /**
   * Las medias de pilar del catálogo DE ESA FILA, con sus etiquetas. `null`
   * cuando su versión no se sabe leer: ahí no hay medias que enseñar.
   */
  pilares: { key: string; label: string; media: number }[] | null;
  catalog_version: number;
  // mig 195: el historial incluye los BORRADORES del entrenador, marcados. Son
  // suyos y tiene que poder volver a ellos — es el camino por el que continúa lo
  // que empezó dirección.
  status: EvaluationStatus;
  report_number: string | null;
}
interface Selected {
  id: string;
  name: string;
  period_start: string;
  period_end: string;
}
type MainTab = 'pendientes' | 'historial';

export default function MonitorEvaluacionesPage() {
  const { profile } = useAuthStore();
  const [mainTab, setMainTab] = useState<MainTab>('pendientes');
  const [pending, setPending] = useState<Pending[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  // «Sin evaluaciones previas» le dice a un entrenador que no ha hecho ninguna.
  // Su historial incluye los borradores: es el camino por el que vuelve a lo
  // que dejó a medias, así que vaciarlo por un fallo de lectura se lo esconde.
  const [errorHistory, setErrorHistory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [selected, setSelected] = useState<Selected | null>(null);

  const loadPending = useCallback(async () => {
    if (!profile) return;
    setLoading(true);
    const { data, error } = await supabase.rpc('formal_evaluations_pending_for_trainer', {
      p_trainer_id: profile.id,
    });
    if (error) {
      console.warn('[MonitorEvaluaciones] RPC pendientes falló', error);
      setPending([]);
    } else {
      setPending((data ?? []) as Pending[]);
    }
    setLoading(false);
  }, [profile]);

  const loadHistory = useCallback(async () => {
    if (!profile) return;
    setLoadingHistory(true);
    setErrorHistory(null);
    const { historial, error } = await listTrainerHistory(profile.id);
    if (error) {
      setErrorHistory(error);
      setHistory([]);
      setLoadingHistory(false);
      return;
    }

    const entries: HistoryEntry[] = historial.map((e) => {
      // EL CATÁLOGO DE LA FILA, NO EL VIGENTE.
      //
      // Esto recomputaba las medias recorriendo el `CATALOG` global y
      // normalizando contra él. Con dos versiones vivas eso no se equivoca por
      // poco: las subs de la versión antigua no existen en el catálogo nuevo,
      // `buildEmptyDetailed` las rellena con DEFAULT_SCORE=5 y el historial
      // enseña un 5.0 redondo que nadie puso, al lado de pilares que ya no
      // están. Y era el único sitio del historial: la ficha y el visor sí
      // pasaban por `getCatalog`.
      const version = e.catalog_version ?? 2;
      let pilares: HistoryEntry['pilares'] = null;
      try {
        const catalogo = getCatalog(version);
        const ds = normalizeDetailed(e.detailed_scores, catalogo);
        pilares = catalogo.pilares.map((p: PillarDef) => {
          const block = ds[p.key];
          return {
            key: p.key,
            label: p.label,
            media: typeof block === 'object' ? pillarAvg(block, p.categories) : 0,
          };
        });
      } catch (err) {
        // Una versión que no sabemos leer no es un error de carga: es una fila
        // que existe y de la que no se pueden dar medias.
        if (!isUnsupportedCatalogVersionError(err)) throw err;
      }
      return {
        id: e.id,
        student_id: e.student_id,
        student_name: e.student?.full_name ?? '',
        period_start: e.period_start,
        period_end: e.period_end,
        pilares,
        catalog_version: version,
        status: e.status,
        report_number: e.report_number,
      };
    });
    setHistory(entries);
    setLoadingHistory(false);
  }, [profile]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);
  useEffect(() => {
    if (mainTab === 'historial') loadHistory();
  }, [mainTab, loadHistory]);

  // ── Formulario ──────────────────────────────────────────────────
  // El entrenador firma siempre sus propios informes: `monitorId` es él. La
  // pantalla de dirección pasará por aquí el entrenador elegido.
  if (selected && profile) {
    return (
      <EvaluationForm
        studentId={selected.id}
        studentName={selected.name}
        monitorId={profile.id}
        periodStart={selected.period_start}
        periodEnd={selected.period_end}
        onBack={() => setSelected(null)}
        onSaved={loadPending}
      />
    );
  }

  // ── Lista (Pendientes / Historial) ──────────────────────────────
  return (
    <div className="space-y-4">
      <h2 className="font-oswald text-2xl text-white">Evaluaciones formales</h2>

      <div className="flex gap-2">
        <button
          onClick={() => setMainTab('pendientes')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-inter text-sm ${
            mainTab === 'pendientes'
              ? 'bg-gold-primary text-black'
              : 'bg-bg-surface text-text-secondary'
          }`}
        >
          <Clock size={16} /> Pendientes ({pending.length})
        </button>
        <button
          onClick={() => setMainTab('historial')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-inter text-sm ${
            mainTab === 'historial'
              ? 'bg-gold-primary text-black'
              : 'bg-bg-surface text-text-secondary'
          }`}
        >
          <CheckCircle size={16} /> Historial
        </button>
      </div>

      {mainTab === 'pendientes' &&
        (loading ? (
          <p className="text-gold-primary font-oswald animate-pulse">Cargando...</p>
        ) : pending.length === 0 ? (
          <p className="text-text-secondary font-inter text-sm" data-testid="pending-empty">
            No hay alumnos con 8 o más sesiones pendientes de evaluar.
          </p>
        ) : (
          <div className="space-y-2" data-testid="pending-list">
            {pending.map((p) => (
              <div
                key={p.student_id}
                className="bg-bg-surface border border-bg-border rounded-xl px-4 py-3 flex items-center justify-between"
              >
                <div>
                  <p className="font-oswald text-white">{p.student_name}</p>
                  {/* La frase la decide el gemelo, no esta página: la misma
                      cola la pintan cuatro sitios y una asignación decía
                      «0 sesiones desde —» (runbook §127). */}
                  <p
                    className={`font-inter text-xs ${
                      esAsignada(p) ? 'text-gold-primary' : 'text-text-secondary'
                    }`}
                    data-testid={`pendiente-motivo-${p.student_id}`}
                  >
                    {motivoDelPendiente(p)}
                  </p>
                </div>
                <button
                  onClick={() =>
                    setSelected({
                      id: p.student_id,
                      name: p.student_name,
                      period_start: p.period_start ?? hoy(),
                      period_end: p.period_end ?? hoy(),
                    })
                  }
                  data-testid={`evaluar-${p.student_id}`}
                  className="bg-gold-primary text-black font-oswald text-sm rounded-lg px-4 py-2"
                >
                  Evaluar
                </button>
              </div>
            ))}
          </div>
        ))}

      {mainTab === 'historial' &&
        (loadingHistory ? (
          <p className="text-gold-primary font-oswald animate-pulse">Cargando...</p>
        ) : errorHistory ? (
          <div className="text-error font-inter text-sm space-y-2" data-testid="history-error">
            <p>No se ha podido cargar tu historial. {errorHistory}</p>
            <button
              className="btn-ghost text-xs"
              onClick={loadHistory}
              data-testid="history-reintentar"
            >
              Reintentar
            </button>
          </div>
        ) : history.length === 0 ? (
          <p className="text-text-secondary font-inter text-sm">Sin evaluaciones previas.</p>
        ) : (
          <div className="space-y-2" data-testid="history-list">
            {history.map((h) => (
              <button
                key={h.id}
                onClick={() =>
                  setSelected({
                    id: h.student_id,
                    name: h.student_name,
                    period_start: h.period_start,
                    period_end: h.period_end ?? h.period_start,
                  })
                }
                className="w-full text-left bg-bg-surface border border-bg-border rounded-xl px-4 py-3"
              >
                <p className="font-oswald text-white flex items-center gap-2">
                  {h.student_name}
                  {h.status === 'draft' && (
                    <span
                      data-testid={`history-draft-${h.id}`}
                      className="font-inter text-[10px] uppercase tracking-wide text-warning border border-warning rounded px-1.5 py-0.5"
                    >
                      Borrador
                    </span>
                  )}
                </p>
                <p className="font-inter text-text-secondary text-xs mb-1">
                  {fmtDate(h.period_start)} → {fmtDate(h.period_end)}
                  {h.report_number ? ` · ${h.report_number}` : ''}
                </p>
                <div className="flex gap-2 flex-wrap">
                  {h.pilares === null ? (
                    <span
                      className="text-xs font-inter px-2 py-0.5 rounded text-text-secondary"
                      style={{ backgroundColor: '#1A1A1A' }}
                      data-testid={`historial-catalogo-no-soportado-${h.id}`}
                    >
                      Catálogo v{h.catalog_version} — sin medias comparables
                    </span>
                  ) : (
                    h.pilares.map((p) => (
                      <span
                        key={p.key}
                        className="text-xs font-inter px-2 py-0.5 rounded"
                        style={{ backgroundColor: '#1A1A1A', color: scoreColor(p.media) }}
                      >
                        {p.label} {p.media.toFixed(1)}
                      </span>
                    ))
                  )}
                </div>
              </button>
            ))}
          </div>
        ))}
    </div>
  );
}
